/**
 * The rules-ingestion module's server state: upload a code document
 * (NEC, IPC, IMC...), watch its parsed sections land, and record the
 * one-time check-back verification per section (user ruling
 * 2026-08-05: every section must be checkable against an
 * authoritative source at least once).
 *
 * WIRING NOTE (2026-08-05): the backend endpoints land with THE RULES
 * REBUILD; until then they 404 and every hook reports that honestly
 * as `backendPending` -- the page says so instead of pretending. The
 * contract below is the agreed shape the wiring implements; these
 * hand-written types are replaced by generated OpenAPI types at that
 * point (api.d.ts is regenerated only when the routes exist).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE, authFetch } from '../../api';

/** One parsed section of an uploaded code document, with its
 * check-back anchor (source file + line span) and verification fact. */
export interface RuleSourceSection {
  id: string;
  code: string; // the book, e.g. "NEC"
  edition: string; // e.g. "2023"
  section: string; // the book's own number, e.g. "240.24(A)"
  title: string;
  text: string;
  source_file: string;
  line_start: number;
  line_end: number;
  classification: string | null; // evaluator shape or null (the NULL set)
  verified_by: string | null; // the one-time check-back event, or null
  verified_at: string | null;
}

export type SectionsState =
  | { backendPending: true }
  | { backendPending: false; sections: RuleSourceSection[] };

export function useRuleSections() {
  return useQuery<SectionsState>({
    queryKey: ['rule-sections'],
    queryFn: async () => {
      const res = await authFetch(`${API_BASE}/v1/rules/sections`);
      if (res.status === 404) return { backendPending: true };
      if (!res.ok) throw new Error(`rule sections: ${res.status}`);
      return {
        backendPending: false,
        sections: (await res.json()) as RuleSourceSection[],
      };
    },
  });
}

/** Upload one code document; the server parses it into sections and
 * the ingest pipeline processes them on arrival (no manual step). */
export function useUploadRuleSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: File; code: string; edition: string }) => {
      const formData = new FormData();
      formData.append('file', input.file);
      formData.append('code', input.code);
      formData.append('edition', input.edition);
      const res = await authFetch(`${API_BASE}/v1/rules/sources`, {
        method: 'POST',
        body: formData,
      });
      if (res.status === 404) return { backendPending: true as const };
      if (!res.ok) throw new Error(`rule source upload: ${res.status}`);
      return { backendPending: false as const };
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['rule-sections'] }),
  });
}

/** Record the one-time human verification of a section against its
 * authoritative source -- an event on the section, it survives
 * re-parses and is never inferred. */
export function useVerifySection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sectionId: string) => {
      const res = await authFetch(`${API_BASE}/v1/rules/sections/${sectionId}/verify`, {
        method: 'POST',
      });
      if (!res.ok && res.status !== 404) throw new Error(`verify section: ${res.status}`);
      return res.status;
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['rule-sections'] }),
  });
}

// --- The ratification surface (rulings in the vault's "Ratification
// Surface" note): the form's pre-fill is Side A law -- verbatim
// quotes only, ambiguity yields an honest null. ---

/** The pre-fill as offered: a value the book itself printed, in the
 * exact words it printed it. The human confirms the QUOTE. */
export interface ThresholdQuote {
  value: number;
  unit: string;
  quote: string;
}

/** The registry (the closed measurement enum) plus, once a
 * measurement is chosen, at most one quoted candidate. */
export interface SectionSuggestion {
  measurements: string[];
  suggestion: ThresholdQuote | null;
}

export function useSectionSuggestion(sectionId: string | null, measurement: string | null) {
  return useQuery<SectionSuggestion | null>({
    queryKey: ['section-suggestion', sectionId, measurement],
    enabled: sectionId !== null,
    queryFn: async () => {
      const params = measurement ? `?measurement=${encodeURIComponent(measurement)}` : '';
      const res = await authFetch(
        `${API_BASE}/v1/rules/sections/${sectionId}/suggestion${params}`,
      );
      if (res.status === 404) return null; // backend pending
      if (!res.ok) throw new Error(`section suggestion: ${res.status}`);
      return (await res.json()) as SectionSuggestion;
    },
  });
}

/** The domain: which elements the rule quantifies over. Typed by
 * construction in the form; matching semantics live SERVER-side. */
export interface RuleDomain {
  category: string | null;
  payload: Record<string, string>;
  payload_regex: Record<string, string>;
}

export interface RatifyRequest {
  name: string;
  jurisdiction: string;
  summary: string;
  measurement: string;
  threshold: { op: 'gt' | 'lt'; value: number; unit: string };
  domain: RuleDomain;
  severity: 'violation' | 'warning' | 'advisory';
  no_data: 'rest' | 'attend' | 'block';
}

export interface RatifiedRule {
  name: string;
  rule: Record<string, unknown>;
  ratified_by: string;
  suggested: ThresholdQuote | null;
}

/** Ratify: the human's completion of a section into a Rule -- the
 * only place numbers enter. Server refusals (verification
 * precondition, seed-name collision, copied summary) surface as
 * error text, never get retried silently. */
export function useRatifySection(sectionId: string) {
  const queryClient = useQueryClient();
  return useMutation<RatifiedRule, Error, RatifyRequest>({
    mutationFn: async (request) => {
      const res = await authFetch(`${API_BASE}/v1/rules/sections/${sectionId}/ratify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
      });
      if (!res.ok) {
        const detail = await res
          .json()
          .then((body: { detail?: unknown }) => body?.detail)
          .catch(() => null);
        throw new Error(typeof detail === 'string' ? detail : `ratify: ${res.status}`);
      }
      return (await res.json()) as RatifiedRule;
    },
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['rule-sections'] }),
  });
}

/** Reference projects for the domain form's observed vocabulary --
 * evidence needs a named corpus (a count against one project is
 * evidence, never truth). */
export function useProjectOptions() {
  return useQuery<string[]>({
    queryKey: ['rule-project-options'],
    queryFn: async () => {
      const res = await authFetch(`${API_BASE}/v1/projects`);
      if (!res.ok) throw new Error(`projects: ${res.status}`);
      const rows: { project_id: string }[] = (await res.json()) ?? [];
      return rows.map((row) => row.project_id);
    },
  });
}

export type DomainPreviewState =
  | { backendPending: true }
  | {
      backendPending: false;
      match_count: number;
      sample: { origin_id: string; element_name: string | null; ifc_class: string | null }[];
    };

/** The evidence panel's wire: the SERVER's own membership semantics
 * (never a client reimplementation). The key carries the domain, so
 * every edit refetches fresh evidence. */
export function useDomainPreview(projectId: string | null, domain: RuleDomain) {
  const domainKey = JSON.stringify(domain);
  return useQuery<DomainPreviewState>({
    queryKey: ['domain-preview', projectId, domainKey],
    enabled: projectId !== null,
    queryFn: async () => {
      const res = await authFetch(`${API_BASE}/v1/rules/domain-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId, domain }),
      });
      if (res.status === 404) return { backendPending: true };
      if (!res.ok) throw new Error(`domain preview: ${res.status}`);
      return { backendPending: false, ...(await res.json()) };
    },
  });
}

const VOCAB_VALUE_CAP = 50;

/** The OBSERVED payload vocabulary of a reference project: the keys
 * that actually exist on elements and the values actually seen.
 * Vocabulary is DATA (safe to observe client-side); domain MATCHING
 * is semantics and stays server-side -- the evidence panel refuses a
 * client reimplementation. */
export function useProjectVocabulary(projectId: string | null) {
  return useQuery<{ keys: string[]; values: Record<string, string[]> }>({
    queryKey: ['rule-domain-vocabulary', projectId],
    enabled: projectId !== null,
    queryFn: async () => {
      const res = await authFetch(
        `${API_BASE}/v1/viewer/elements?project_id=${encodeURIComponent(projectId!)}`,
      );
      if (!res.ok) throw new Error(`vocabulary elements: ${res.status}`);
      const elements: { raw_attributes?: Record<string, unknown> }[] = (await res.json()) ?? [];
      const values = new Map<string, Set<string>>();
      for (const el of elements) {
        for (const [key, value] of Object.entries(el.raw_attributes ?? {})) {
          if (typeof value !== 'string' && typeof value !== 'number') continue;
          const seen = values.get(key) ?? new Set<string>();
          if (seen.size < VOCAB_VALUE_CAP) seen.add(String(value));
          values.set(key, seen);
        }
      }
      return {
        keys: Array.from(values.keys()).sort(),
        values: Object.fromEntries(
          Array.from(values.entries(), ([key, set]) => [key, Array.from(set).sort()]),
        ),
      };
    },
  });
}
